'use client';

import { formatDuration, getVimeoVideoId } from '@/app/lib/media-helper';
import Player, { VimeoEmbedParameters } from '@vimeo/player';
import { useEffect, useMemo, useRef, useState } from 'react';
import { defaultVimeoPlayerOptions } from '@workspace/constants';
import { theme } from '../../../theme';

// Brand color for the Vimeo player controls, matching --color-primary per theme.
const brandControlColor = theme === 'redRec' ? '#004639' : '#CD1543';

export function useVimeoPlayer(
  video?: string,
  containerRef?: React.RefObject<HTMLDivElement | null>,
  vimeoPlayerOptions: Omit<VimeoEmbedParameters, 'id'> = defaultVimeoPlayerOptions,
) {
  const playerRef = useRef<Player | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<string | null>(null);

  const finalVimeoPlayerOptions = useMemo(
    () => ({
      ...defaultVimeoPlayerOptions,
      ...vimeoPlayerOptions,
      color: brandControlColor,
    }),
    [vimeoPlayerOptions],
  );

  useEffect(() => {
    if (!video || !containerRef?.current) return;

    const videoId = getVimeoVideoId(video);
    if (!videoId) return;

    const player = new Player(containerRef.current, {
      id: +videoId,
      ...finalVimeoPlayerOptions,
    });

    playerRef.current = player;

    const fillAndCenter = () => {
      const wrapper = containerRef.current?.firstElementChild as HTMLElement | null;
      const iframe = wrapper?.querySelector('iframe') as HTMLIFrameElement | null;

      if (wrapper) {
        wrapper.style.position = 'absolute';
        wrapper.style.inset = '0';
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        wrapper.style.padding = '0'; // kill 56.25% padding trick
        wrapper.style.overflow = 'hidden';
      }
      if (iframe) {
        iframe.className = 'absolute inset-0 w-full h-full';
        iframe.setAttribute('allow', 'fullscreen; picture-in-picture');
      }
    };

    player.ready().then(() => {
      setIsReady(true);
      player.getDuration().then((d) => setDuration(formatDuration(d)));
      // Ensure brand control color is applied
      if (finalVimeoPlayerOptions?.color) {
        // setColor expects a hex string without '#'
        void player.setColor(finalVimeoPlayerOptions.color as string).catch(() => null);
      }
      // Compute aspect ratio based on the actual video dimensions
      Promise.all([player.getVideoWidth(), player.getVideoHeight()])
        .then(([w, h]) => {
          if (w && h) {
            setAspectRatio(`${w} / ${h}`);
          }
        })
        .catch(() => {
          // ignore, fallback to null
        });
      fillAndCenter();
      const mo = new MutationObserver(fillAndCenter);
      mo.observe(containerRef.current!, { childList: true, subtree: true });
    });

    player.on('play', () => {
      setIsPlaying(true);
      if (finalVimeoPlayerOptions?.color) {
        void player.setColor(finalVimeoPlayerOptions.color as string).catch(() => null);
      }
    });
    player.on('pause', () => setIsPlaying(false));
    player.on('ended', () => setIsPlaying(false));
    player.on('loaded', () => {
      if (finalVimeoPlayerOptions?.color) {
        void player.setColor(finalVimeoPlayerOptions.color as string).catch(() => null);
      }
    });
  }, [video, containerRef, vimeoPlayerOptions, finalVimeoPlayerOptions]);
  const play = async () => {
    if (!playerRef.current || !isReady) return;
    await playerRef.current.play();
  };
  const pause = async () => {
    if (!playerRef.current || !isReady) return;
    await playerRef.current.pause();
  };

  const reset = async () => {
    if (!playerRef.current || !isReady) return;
    await pause();
    await playerRef.current.setCurrentTime(0);
  };
  const togglePlay = async () => {
    if (!playerRef.current || !isReady) return;
    isPlaying ? await pause() : await play();
  };

  return {
    isPlaying,
    duration,
    togglePlay,
    isReady,
    play,
    pause,
    reset,
    aspectRatio,
  };
}
